import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { Icon } from "@iconify/react";
import { Modal, Button, Form } from "react-bootstrap";
import toast from "react-hot-toast";
import {
  node_imageBaseUrl,
  formatDateToDDMMYYYY,
  imageCustomOrderUrl,
  getCurrentPinType,
  getQuoteInvoiceListScope,
  resolveQuoteListApiType,
  QUOTE_INVOICE_LIST_SCOPE_EVENT,
} from "./const";
import {
  isErpApiSuccess,
  listRepairingOrders,
  listBankAccounts,
  showRepairingOrder,
  settlementRepairingOrder,
} from "../erpClient";
import { hasPermission, PERMISSIONS } from "../utils/permissions";
import useIsCurrentFinancialYear, {
  FY_ADD_DISABLED_TITLE,
} from "../hooks/useIsCurrentFinancialYear";
import {
  normalizeRepairingOrderListFromResponse,
  normalizeRepairingOrderRow,
  createRepairingOrderIdempotencyKey,
} from "../utils/repairingOrdersErpAdapter";
import { useNavigate } from "react-router-dom";
import AssignKarigarModal from "../components/AssignKarigarModal";
import ReceiveUpdateModal from "../components/ReceiveUpdateModal";
import ProductReceivedModal from "../components/ProductReceivedModal";
import CancelOrderModal from "../components/CancelOrderModal";
import { PDFViewer } from "@react-pdf/renderer";
import RepairingSlipPDF from "../components/RepairingSlipPDF";
import Pagination from "../components/Pagination";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import { formatIndianRupee } from "../utils/formatLiveRate";
import { createErpSocket } from "../utils/socketIo";
import { parseApiPagination } from "../utils/parseApiPagination";
import { useVisitedPageCache, visitedPageKey } from "../utils/visitedPageCache";
import Select from "react-select";

const SETTLEMENT_METHOD_OPTIONS = [
  { value: "cash", label: "Cash" },
  { value: "cheque", label: "Cheque" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "upi", label: "UPI" },
  { value: "card", label: "Card" },
  { value: "old_gold", label: "Old Gold" },
  { value: "old_silver", label: "Old Silver" },
  { value: "kasar", label: "Kasar" },
  { value: "scheme", label: "Scheme" },
  { value: "order", label: "Order" },
  { value: "os", label: "OS" },
  { value: "wallet", label: "Wallet" },
];

const SETTLEMENT_METHOD_LABELS = {
  cash: "Cash",
  cheque: "Cheque",
  bank_transfer: "Bank Transfer",
  upi: "UPI",
  card: "Card",
  old_gold: "Old Gold",
  old_silver: "Old Silver",
  kasar: "Kasar",
  scheme: "Scheme",
  order: "Order",
  os: "OS",
  wallet: "Wallet",
};

const createDefaultSettlementRows = () => [
  {
    id: 1,
    pay_mode: "",
    type: "",
    remarks: "",
    amount: "",
  },
];

const parseNumericValue = (value) => {
  if (value === null || value === undefined) {
    return 0;
  }
  if (typeof value === "number") {
    return Number.isNaN(value) ? 0 : value;
  }
  const cleaned = String(value).replace(/[^0-9.-]/g, "");
  const numeric = parseFloat(cleaned);
  return Number.isNaN(numeric) ? 0 : numeric;
};

const getRepairingOrderPaidAmount = (order = {}) => {
  const fromApi = order.adv_paid ?? order.advance_paid ?? order.advPaid;
  if (fromApi != null && fromApi !== "") {
    return parseNumericValue(fromApi);
  }
  const payments = order.payments || order.payment_details || [];
  if (!Array.isArray(payments) || payments.length === 0) {
    return 0;
  }
  return payments.reduce(
    (sum, payment) =>
      sum +
      parseNumericValue(
        payment.amount ??
        payment.amt ??
        payment.aavak_amt ??
        payment.total_amount ??
        0,
      ),
    0,
  );
};

const getRepairingOrderRemainingAmount = (order = {}) => {
  if (order.remaining != null && order.remaining !== "") {
    return parseNumericValue(order.remaining);
  }
  if (order.remaining_amount != null && order.remaining_amount !== "") {
    return parseNumericValue(order.remaining_amount);
  }
  const total = parseNumericValue(
    order.grand_total ?? order.final_amount ?? order.total_amount ?? 0,
  );
  return Math.max(total - getRepairingOrderPaidAmount(order), 0);
};

function RepairingOrder({ masterGoldRate, masterSilverRate }) {
  const { buttonClass, tableClass, cardClass } = useButtonTableStyle();
  const isCurrentFinancialYear = useIsCurrentFinancialYear();
  const [canAddRepairingOrder, setCanAddRepairingOrder] = useState(() =>
    hasPermission(PERMISSIONS.REPAIRING_ORDER_ADD),
  );
  const [canEditRepairingOrder, setCanEditRepairingOrder] = useState(() =>
    hasPermission(PERMISSIONS.REPAIRING_ORDER_EDIT),
  );
  const [canCancelOrder, setCanCancelOrder] = useState(() =>
    hasPermission(PERMISSIONS.ORDER_CANCEL),
  );

  useEffect(() => {
    const syncPerms = () => {
      setCanAddRepairingOrder(hasPermission(PERMISSIONS.REPAIRING_ORDER_ADD));
      setCanEditRepairingOrder(hasPermission(PERMISSIONS.REPAIRING_ORDER_EDIT));
      setCanCancelOrder(hasPermission(PERMISSIONS.ORDER_CANCEL));
    };
    window.addEventListener("permissionsUpdated", syncPerms);
    window.addEventListener("erpAuthUpdated", syncPerms);
    return () => {
      window.removeEventListener("permissionsUpdated", syncPerms);
      window.removeEventListener("erpAuthUpdated", syncPerms);
    };
  }, []);
  const [orders, setOrders] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [quoteListScope, setQuoteListScope] = useState(
    getQuoteInvoiceListScope,
  );
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // Add loading state
  const [selectedItems, setSelectedItems] = useState({}); // Track selected items by order id and item id
  const [showAssignKarigarModal, setShowAssignKarigarModal] = useState(false);
  const [assignKarigarOrderData, setAssignKarigarOrderData] = useState(null);
  const [showReceiveUpdateModal, setShowReceiveUpdateModal] = useState(false);
  const [receiveUpdateOrderData, setReceiveUpdateOrderData] = useState(null);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [selectedOrderForPdf, setSelectedOrderForPdf] = useState(null);
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [showCancelOrderModal, setShowCancelOrderModal] = useState(false);
  const [cancelOrderData, setCancelOrderData] = useState(null);
  const [cancelSelectedItems, setCancelSelectedItems] = useState([]);
  const [isWholeOrderCancel, setIsWholeOrderCancel] = useState(false);
  const [socket, setSocket] = useState(null);
  const navigate = useNavigate();
  const isFetching = useRef(false);
  const hasFetchedBanks = useRef(false);
  const [bankAccountsList, setBankAccountsList] = useState([]);
  const [showPaymentSettlementModal, setShowPaymentSettlementModal] =
    useState(false);
  const [selectedOrderForSettlement, setSelectedOrderForSettlement] =
    useState(null);
  const [settlementOrderDetails, setSettlementOrderDetails] = useState(null);
  const [existingSettlementPayments, setExistingSettlementPayments] = useState(
    [],
  );
  const [settlementRows, setSettlementRows] = useState(
    createDefaultSettlementRows(),
  );
  const [isSettlementLoading, setIsSettlementLoading] = useState(false);
  const [isSettlementSaving, setIsSettlementSaving] = useState(false);
  const [isPreviousPaymentsExpanded, setIsPreviousPaymentsExpanded] =
    useState(false);
  const settlementIdempotencyKeyRef = useRef(null);
  const [showProductReceivedModal, setShowProductReceivedModal] =
    useState(false);
  const [productReceivedOrder, setProductReceivedOrder] = useState(null);
  const [productReceivedItems, setProductReceivedItems] = useState([]);

  const pageCache = useVisitedPageCache();

  const fetchOrders = useCallback(
    async (page = 1, limit = 10, search = "", force = false) => {
      const listType = resolveQuoteListApiType("list");
      const cacheKey = visitedPageKey([listType, page, limit, search.trim()]);
      if (force) pageCache.clear();
      const cached = force ? null : pageCache.read(cacheKey);
      if (cached) {
        setOrders(cached.items);
        setTotalCount(cached.total);
        setIsLoading(false);
        return;
      }
      if (isFetching.current) return;
      isFetching.current = true;
      setIsLoading(true);
      try {
        const res = await listRepairingOrders({
          search: search.trim(),
          page,
          limit,
          type: listType,
        });
        const rows = normalizeRepairingOrderListFromResponse(res);
        if (isErpApiSuccess(res)) {
          const sorted = [...rows].sort((a, b) => Number(b.id) - Number(a.id));
          const total = parseApiPagination(res, sorted.length).total;
          setOrders(sorted);
          setTotalCount(total);
          pageCache.write(cacheKey, { items: sorted, total });
        } else {
          setOrders([]);
          setTotalCount(0);
          toast.error(res?.message || "No repairing orders found");
        }
      } catch (error) {
        setOrders([]);
        setTotalCount(0);
        toast.error(
          error.response?.data?.message || "Failed to fetch repairing orders",
        );
      } finally {
        setIsLoading(false);
        isFetching.current = false;
      }
    },
    [quoteListScope],
  );

  const fetchBankAccounts = async () => {
    try {
      const response = await listBankAccounts();
      if (response?.success && Array.isArray(response.data?.bank_accounts)) {
        const rows = response.data.bank_accounts
          .map((bank) => {
            const chartAccountId =
              bank.account_id ??
              bank.accountId ??
              bank.linked_account_id ??
              bank.accounts_id ??
              bank.id;
            return {
              id: chartAccountId,
              bank_name: bank.bank_name || "",
              account_number: bank.account_number || "",
              account_type: bank.account_type || "",
              status: bank.status,
            };
          })
          .filter((bank) => bank.status === 1);
        rows.sort((a, b) => Number(b.id) - Number(a.id));
        setBankAccountsList(rows);
      } else {
        setBankAccountsList([]);
      }
    } catch (e) {
      console.error("RepairingOrder: fetchBankAccounts", e);
      setBankAccountsList([]);
    }
  };

  const ensureBankAccountsForCancel = async () => {
    if (hasFetchedBanks.current) return;
    await fetchBankAccounts();
    hasFetchedBanks.current = true;
  };

  useEffect(() => {
    if (
      showPaymentSettlementModal ||
      showCancelOrderModal ||
      showProductReceivedModal
    ) {
      ensureBankAccountsForCancel();
    }
  }, [
    showPaymentSettlementModal,
    showCancelOrderModal,
    showProductReceivedModal,
  ]);

  useEffect(() => {
    const timeoutId = setTimeout(
      () => {
        fetchOrders(currentPage, itemsPerPage, searchQuery);
      },
      searchQuery ? 200 : 0,
    );
    return () => clearTimeout(timeoutId);
  }, [currentPage, itemsPerPage, searchQuery, fetchOrders]);

  // Navbar Quotation | All → list vs mixed
  useEffect(() => {
    const handleScopeChange = (event) => {
      const next =
        event?.detail?.scope === "all" ? "all" : getQuoteInvoiceListScope();
      setQuoteListScope(next);
      setCurrentPage(1);
    };
    window.addEventListener(QUOTE_INVOICE_LIST_SCOPE_EVENT, handleScopeChange);
    return () => {
      window.removeEventListener(
        QUOTE_INVOICE_LIST_SCOPE_EVENT,
        handleScopeChange,
      );
    };
  }, []);

  // Listen for API URL changes and refetch data
  useEffect(() => {
    const handleApiUrlChange = (event) => {
      console.log(
        "API URL changed in RepairingOrder, refetching orders...",
        event.detail,
      );
      hasFetchedBanks.current = false;
      setCurrentPage(1);
      fetchOrders(1, itemsPerPage, searchQuery, true);
    };

    window.addEventListener("apiUrlChanged", handleApiUrlChange);

    return () => {
      window.removeEventListener("apiUrlChanged", handleApiUrlChange);
    };
  }, [itemsPerPage, searchQuery, fetchOrders]);

  // Socket.IO listener for real-time updates
  useEffect(() => {
    const newSocket = createErpSocket();
    setSocket(newSocket);

    // Connection event logging
    newSocket.on("connect", () => {
      // console.log("RepairingOrder: Connected to socket server");
    });

    newSocket.on("disconnect", (reason) => {
      // console.log("RepairingOrder: Disconnected from socket server:", reason);
    });

    newSocket.on("connect_error", (error) => {
      console.error("RepairingOrder: Socket connection error:", error);
    });

    newSocket.on("repairing_order_created", (data) => {
      if (!data?.repairing_order) return;

      console.log("[socket] repairing_order_created (raw):", data);
      console.log(
        "[socket] repairing_order_created.repairing_order (raw):",
        data.repairing_order,
      );

      // Normalize socket payload to match list API shape
      const newOrder = normalizeRepairingOrderRow(data.repairing_order);

      console.log(
        "[socket] repairing_order_created (mapped newOrder):",
        newOrder,
      );

      setOrders((prev) => {
        const exists = prev.some(
          (order) => String(order.id) === String(newOrder.id),
        );
        if (exists) {
          return prev.map((order) =>
            String(order.id) === String(newOrder.id) ? newOrder : order,
          );
        }
        return [newOrder, ...prev].sort((a, b) => Number(b.id) - Number(a.id));
      });
    });

    newSocket.on("repairing_order_updated", (data) => {
      if (!data?.repairing_order || !data.repairing_order.id) {
        console.warn("Invalid repairing_order_updated socket data:", data);
        return;
      }

      console.log("[socket] repairing_order_updated (raw):", data);
      console.log(
        "[socket] repairing_order_updated.repairing_order (raw):",
        data.repairing_order,
      );

      const normalized = normalizeRepairingOrderRow(data.repairing_order);
      setOrders((prev) =>
        prev.map((order) =>
          String(order.id) === String(normalized.id) ? normalized : order,
        ),
      );
      console.log("[socket] repairing_order_updated (normalized):", normalized);
    });

    return () => {
      newSocket.off("repairing_order_created");
      newSocket.off("repairing_order_updated");
      newSocket.disconnect();
    };
  }, []);

  // Handle item selection
  const handleItemSelect = (orderId, itemId, isSelected) => {
    setSelectedItems((prev) => {
      const newSelection = { ...prev };
      if (!newSelection[orderId]) {
        newSelection[orderId] = {};
      }

      if (isSelected) {
        newSelection[orderId][itemId] = true;
      } else {
        delete newSelection[orderId][itemId];
        // Clean up empty order entries
        if (Object.keys(newSelection[orderId]).length === 0) {
          delete newSelection[orderId];
        }
      }

      return newSelection;
    });
  };

  // Check if an item is selected
  const isItemSelected = (orderId, itemId) => {
    return Boolean(selectedItems[orderId]?.[itemId]);
  };

  // Check if karigar is assigned (karigar_id not 0 means karigar assigned)
  const isKarigarAssigned = (item) => {
    const karigarId = item.karigar_id;
    if (karigarId === null || karigarId === undefined || karigarId === "") {
      return false;
    }
    return karigarId !== 0 && karigarId !== "0" && parseInt(karigarId) !== 0;
  };

  // Check if an item is already received (status = 2 means received)
  const isItemReceived = (item) => {
    const status = item.status;
    const isReceivedStatus =
      status === 2 || status === "2" || parseInt(status, 10) === 2;
    // Per API contract: treat as received only when karigar is assigned + status=2.
    return isKarigarAssigned(item) && isReceivedStatus;
  };

  const isProductDeliveredToCustomer = (item) => {
    if (item.product_delivery_date || item.product_delivered_date) {
      return true;
    }
    if (
      item.product_delivered === true ||
      item.product_delivered === 1 ||
      item.product_delivered === "1"
    ) {
      return true;
    }
    const status = parseInt(item.status, 10);
    return status === 4;
  };

  const getProductReceivedEligibleItems = (order) => {
    const items = Array.isArray(order?.repairing_order_entry)
      ? order.repairing_order_entry
      : [];
    return items.filter(
      (item) =>
        isItemReceived(item) &&
        !isProductDeliveredToCustomer(item) &&
        !isItemCancelled(item),
    );
  };

  const handleOpenProductReceivedModal = (order) => {
    const eligible = getProductReceivedEligibleItems(order);
    if (eligible.length === 0) {
      toast.error("No items ready for customer product handover");
      return;
    }
    setProductReceivedOrder(order);
    setProductReceivedItems(eligible);
    setShowProductReceivedModal(true);
  };

  const handleProductReceivedSuccess = () => {
    setShowProductReceivedModal(false);
    setProductReceivedOrder(null);
    setProductReceivedItems([]);
    fetchOrders(currentPage, itemsPerPage, searchQuery, true);
  };

  const isItemCancelled = (item) => {
    const status = item.status;
    return status === 3 || status === "3" || parseInt(status) === 3;
  };

  const getOrderStatusMeta = (order) => {
    const items = Array.isArray(order?.repairing_order_entry)
      ? order.repairing_order_entry
      : [];
    const activeItems = items.filter((item) => !isItemCancelled(item));
    const hasActiveItems = activeItems.length > 0;

    // Whole order cancelled: every entry is cancelled (status can still be 1 until refresh)
    if (items.length > 0 && !hasActiveItems) {
      return {
        label: "Cancelled",
        className: "bg-danger-subtle text-danger",
      };
    }

    const allDelivered =
      hasActiveItems &&
      activeItems.every((item) => isProductDeliveredToCustomer(item));

    if (allDelivered) {
      return {
        label: "Completed",
        className: "bg-success-subtle text-success",
      };
    }

    // All items received from karigar — still allow Product Delivery / Move to Sales
    const allReceived =
      hasActiveItems && activeItems.every((item) => isItemReceived(item));

    if (allReceived) {
      return {
        label: "Received",
        className: "bg-info-subtle text-info",
      };
    }

    const status = order?.status;
    const isCancelled =
      status === "0" || status === 0 || status === "3" || status === 3;
    if (isCancelled) {
      return {
        label: "Cancelled",
        className: "bg-danger-subtle text-danger",
      };
    }

    const isActive = status === "1" || status === 1;
    return {
      label: isActive ? "Active" : "Inactive",
      className: isActive
        ? "bg-success-subtle text-success"
        : "bg-danger-subtle text-danger",
    };
  };

  // Check if order is editable: not editable if ANY item has karigar assigned
  const isOrderEditable = (order) => {
    const items = order?.repairing_order_entry || [];
    return !items.some((it) => isKarigarAssigned(it));
  };

  // Get selected items count for display
  const getSelectedItemsCount = () => {
    let count = 0;
    Object.values(selectedItems).forEach((orderItems) => {
      count += Object.keys(orderItems).length;
    });
    return count;
  };

  // Build a flat list of selected item objects from current orders
  const getSelectedItemsFlat = () => {
    const selected = [];
    Object.keys(selectedItems).forEach((orderId) => {
      const order = orders.find((o) => String(o.id) === String(orderId));
      if (!order || !Array.isArray(order.repairing_order_entry)) return;
      const selectedItemIds = Object.keys(selectedItems[orderId] || {});
      order.repairing_order_entry.forEach((item) => {
        if (selectedItemIds.includes(String(item.id))) {
          selected.push(item);
        }
      });
    });
    return selected;
  };

  // Determine whether all selected items are eligible for karigar assignment
  const canAssignSelectedItems = () => {
    const selected = getSelectedItemsFlat();
    if (selected.length === 0) return false;
    return selected.every(
      (item) =>
        !isKarigarAssigned(item) &&
        !isItemReceived(item) &&
        !isItemCancelled(item),
    );
  };

  // Sort orders by id (or created_at) descending for most recent first
  const sortedOrders = [...orders].sort((a, b) => {
    // Prefer created_at if available, else fallback to id
    if (a.created_at && b.created_at) {
      return new Date(b.created_at) - new Date(a.created_at);
    }
    return Number(b.id) - Number(a.id);
  });

  // Client-side status filter on the current server page
  const filteredOrders = sortedOrders.filter((order) => {
    const status = order.status || "";
    const matchesStatus =
      statusFilter === "" ||
      (statusFilter === "Active" && (status === "1" || status === 1)) ||
      (statusFilter === "Inactive" && (status === "0" || status === 0)) ||
      status === "";
    return matchesStatus;
  });

  const handlePageChange = (newPage) => {
    const totalPages = Math.ceil(totalCount / itemsPerPage);
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const displayedOrders = filteredOrders;

  // View modal logic
  const handleViewClick = (order) => {
    setSelectedOrder(order);
    setShowViewModal(true);
  };

  // Add stub for Edit button
  const handleEditClick = (order) => {
    // Navigate to edit page and pass only the repairing order id
    navigate("/editrepairingorder", {
      state: {
        repairingOrderId: order.id,
      },
    });
  };

  const handleOpenPdfModal = async (order) => {
    if (!order?.id) {
      toast.error("Invalid repairing order selected");
      return;
    }

    setSelectedOrderForPdf(order);
    setShowPdfModal(true);
    setIsPdfLoading(true);

    try {
      const showRes = await showRepairingOrder({ id: Number(order.id) });
      if (!isErpApiSuccess(showRes)) {
        toast.error(
          showRes?.message || "Failed to fetch repairing order details",
        );
        return;
      }

      const payload = showRes?.data ?? showRes ?? {};
      const details = payload?.repairing_order_details ?? payload;
      let fullOrder = null;

      if (Array.isArray(details)) {
        fullOrder =
          details.find((row) => String(row?.id) === String(order.id)) ||
          details[0] ||
          null;
      } else if (details && typeof details === "object") {
        fullOrder =
          details?.repairing_order_entry || details?.entries || details?.items
            ? details
            : payload?.repairing_order || details;
      }

      if (fullOrder) {
        setSelectedOrderForPdf(normalizeRepairingOrderRow(fullOrder));
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
        "Failed to fetch repairing order details",
      );
    } finally {
      setIsPdfLoading(false);
    }
  };

  const resolveRepairingOrderShowDetails = (showRes, orderId) => {
    const payload = showRes?.data ?? showRes ?? {};
    const details = payload?.repairing_order_details ?? payload;
    let fullOrder = null;

    if (Array.isArray(details)) {
      fullOrder =
        details.find((row) => String(row?.id) === String(orderId)) ||
        details[0] ||
        null;
    } else if (details && typeof details === "object") {
      fullOrder =
        details?.repairing_order_entry || details?.entries || details?.items
          ? details
          : payload?.repairing_order || details;
    }

    if (!fullOrder) return null;

    const normalized = normalizeRepairingOrderRow(fullOrder);
    const payments =
      fullOrder.payments ||
      fullOrder.payment_details ||
      payload?.payments ||
      payload?.payment_details ||
      [];
    return {
      ...normalized,
      payments: Array.isArray(payments) ? payments : [],
    };
  };

  const resetPaymentSettlementState = () => {
    setSettlementRows(createDefaultSettlementRows());
    setExistingSettlementPayments([]);
    setSettlementOrderDetails(null);
    setSelectedOrderForSettlement(null);
    setIsSettlementLoading(false);
    setIsSettlementSaving(false);
    setIsPreviousPaymentsExpanded(false);
    settlementIdempotencyKeyRef.current = null;
  };

  const addSettlementRow = () => {
    setSettlementRows((prevRows) => [
      ...prevRows,
      {
        id: prevRows.length + 1,
        pay_mode: "",
        type: "",
        remarks: "",
        amount: "",
      },
    ]);
  };

  const handleSettlementRowChange = (rowId, field, value) => {
    setSettlementRows((prevRows) =>
      prevRows.map((row) => {
        if (row.id !== rowId) return row;
        if (field === "pay_mode" && value === "kasar") {
          return { ...row, pay_mode: value, type: "" };
        }
        return { ...row, [field]: value };
      }),
    );
  };

  const handleSettlementAmountFocus = (rowId) => {
    if (
      settlementRows.length > 0 &&
      rowId === settlementRows[settlementRows.length - 1].id
    ) {
      addSettlementRow();
    }
  };

  const handleSettlementRowRemove = (rowId) => {
    setSettlementRows((prevRows) => {
      if (prevRows.length === 1) return prevRows;
      const filtered = prevRows
        .filter((row) => row.id !== rowId)
        .map((row, index) => ({ ...row, id: index + 1 }));
      return filtered.length > 0 ? filtered : createDefaultSettlementRows();
    });
  };

  const getBankNameById = (id) => {
    if (id === null || id === undefined || id === "" || id === "None") {
      return "-";
    }
    const bank = bankAccountsList.find((b) => String(b.id) === String(id));
    return bank
      ? bank.bank_name || bank.account_number || `Bank ${bank.id}`
      : "-";
  };

  const handleOpenPaymentSettlementModal = async (order) => {
    if (!order) return;

    setSelectedOrderForSettlement(order);
    setSettlementRows(createDefaultSettlementRows());
    setExistingSettlementPayments([]);
    setSettlementOrderDetails(null);
    setIsPreviousPaymentsExpanded(false);
    setShowPaymentSettlementModal(true);
    setIsSettlementLoading(true);

    try {
      const response = await showRepairingOrder({ id: Number(order.id) });
      const details = resolveRepairingOrderShowDetails(response, order.id);

      if (isErpApiSuccess(response) && details) {
        setSettlementOrderDetails(details);
        setExistingSettlementPayments(details.payments || []);
      } else {
        toast.error(
          response?.message || "Failed to load repairing order details",
        );
        setShowPaymentSettlementModal(false);
        resetPaymentSettlementState();
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
        error.message ||
        "Failed to load repairing order details",
      );
      setShowPaymentSettlementModal(false);
      resetPaymentSettlementState();
    } finally {
      setIsSettlementLoading(false);
    }
  };

  const handleClosePaymentSettlementModal = () => {
    if (isSettlementSaving) return;
    setShowPaymentSettlementModal(false);
    resetPaymentSettlementState();
  };

  const bankOptions = useMemo(
    () =>
      bankAccountsList.map((bank) => ({
        value: String(bank.id),
        label: bank.bank_name || bank.account_number || `Bank ${bank.id}`,
      })),
    [bankAccountsList],
  );

  const settlementTotalAmount = useMemo(() => {
    if (settlementOrderDetails) {
      const totalValue =
        settlementOrderDetails.grand_total ??
        settlementOrderDetails.final_amount ??
        settlementOrderDetails.total_amount;
      if (totalValue !== undefined && totalValue !== null) {
        return parseNumericValue(totalValue);
      }
    }
    if (selectedOrderForSettlement) {
      return parseNumericValue(
        selectedOrderForSettlement.grand_total ??
        selectedOrderForSettlement.final_amount ??
        0,
      );
    }
    return 0;
  }, [settlementOrderDetails, selectedOrderForSettlement]);

  const settlementRemainingAmount = useMemo(() => {
    if (settlementOrderDetails) {
      return getRepairingOrderRemainingAmount(settlementOrderDetails);
    }
    if (selectedOrderForSettlement) {
      return getRepairingOrderRemainingAmount(selectedOrderForSettlement);
    }
    return 0;
  }, [settlementOrderDetails, selectedOrderForSettlement]);

  const newSettlementAmountTotal = useMemo(
    () =>
      settlementRows.reduce(
        (sum, row) => sum + parseNumericValue(row.amount),
        0,
      ),
    [settlementRows],
  );

  const handleSavePaymentSettlement = async () => {
    if (!selectedOrderForSettlement) {
      toast.error("Unable to determine the selected order");
      return;
    }

    if (isSettlementLoading || isSettlementSaving) {
      return;
    }

    const newPayments = settlementRows
      .filter((row) => row.pay_mode && parseNumericValue(row.amount) > 0)
      .map((row) => ({
        pay_mode_key: row.pay_mode,
        pay_mode: SETTLEMENT_METHOD_LABELS[row.pay_mode] || row.pay_mode || "",
        bank_type: row.pay_mode === "kasar" ? "" : row.type || "",
        remarks: row.remarks || "",
        amt: parseNumericValue(row.amount),
      }));

    if (newPayments.length === 0) {
      toast.error("Enter at least one amount to settle");
      return;
    }

    if (newSettlementAmountTotal - settlementRemainingAmount > 0.05) {
      toast.error("Settlement amount exceeds remaining balance");
      return;
    }

    setIsSettlementSaving(true);

    try {
      if (!settlementIdempotencyKeyRef.current) {
        settlementIdempotencyKeyRef.current =
          createRepairingOrderIdempotencyKey();
      }

      const erpPayments = newPayments
        .map((p) => {
          const remarks = p.remarks?.trim() ? p.remarks.trim() : null;
          if (p.pay_mode_key === "kasar") {
            return {
              payment_mode: "Kasar",
              amount: Number(p.amt) || 0,
              remarks,
            };
          }
          return {
            bank_account_id: Number(p.bank_type) || undefined,
            amount: Number(p.amt) || 0,
            payment_mode: p.pay_mode || null,
            remarks,
          };
        })
        .filter((p) => p.amount > 0);

      const response = await settlementRepairingOrder({
        repairing_order_id: selectedOrderForSettlement.id,
        idempotency_key: settlementIdempotencyKeyRef.current,
        payments: erpPayments,
      });

      if (isErpApiSuccess(response)) {
        settlementIdempotencyKeyRef.current = null;
        toast.success("Amount settled successfully");
        setShowPaymentSettlementModal(false);
        resetPaymentSettlementState();
        fetchOrders(currentPage, itemsPerPage, searchQuery, true);
      } else {
        toast.error(response?.message || "Failed to save settlement details");
      }
    } catch (error) {
      console.error("Error saving settlement:", error);
      toast.error(
        error.response?.data?.message ||
        error.message ||
        "Failed to save settlement",
      );
    } finally {
      setIsSettlementSaving(false);
    }
  };

  const hasExistingSettlementPayments = existingSettlementPayments.length > 0;
  const disableSettlementSaveButton =
    isSettlementSaving || isSettlementLoading || newSettlementAmountTotal <= 0;

  // Navigate to Sales with repairing order data (similar to CustomOrderEntryTable → Sales)
  const handleMoveToSales = (order) => {
    // Filter only received items for sales
    const receivedItems = Array.isArray(order.repairing_order_entry)
      ? order.repairing_order_entry.filter((item) => isItemReceived(item))
      : [];

    if (receivedItems.length === 0) {
      toast.error("No received items available to move to Sales");
      return;
    }

    // Map repairing order structure to custom order structure for Sales page compatibility
    const mappedOrderEntries = receivedItems.map((item, index) => ({
      ...item,
      product_group:
        item.product_group_name ||
        item.product_type ||
        item.product_group ||
        "",
      product_group_id: item.product_group,
      design: item.design || "",
      size: item.size || "",
      pcs: item.pcs || "",
      gwt: item.current_weight || item.gwt || "",
      lwt: item.lwt || "",
      nwt: item.exp_weight || item.nwt || "",
      lbr_percent: item.lbr_percentage || "",
      lbr_rate: item.lbr_rate || "",
      lbr_amt: item.lbr_amount || "",
      othamt: item.other_amount || "",
      totalamt: item.totalamt || item.final_amount || "",
      charges: item.repairing_order_charges || [],
      tag_no: item.tag_no || "",
    }));

    // Create custom order data structure
    const customOrderData = {
      ...order,
      user_id: order.user_id || order.by_user,
      exp_delivery: order.exp_delivery || "",
      final_amount: order.final_amount || "",
      round_off: order.round_off || "",
      note: order.note || "",
      co_number: order.token_no || "", // Use token_no as order number
    };

    // console.log("\n📋 Custom Order Data:");
    // console.log(customOrderData);

    const navigationState = {
      repairingOrderId: order.id,
      repairingOrderData: order,
      // Map to custom order structure for Sales page compatibility
      customOrderId: null, // Don't pass repairing order ID as custom order ID
      customOrderTable: mappedOrderEntries,
      customOrderData: customOrderData,
      customOrderPayments: [],
      isFromRepairingOrder: true, // Flag to identify repairing orders
    };

    // console.log("\n🚀 FINAL NAVIGATION STATE TO SALES:");
    // console.log(navigationState);
    // console.log("===========================================\n");

    navigate("/sales", {
      state: navigationState,
    });
  };

  // Handle bulk assign karigar for selected items
  const handleBulkAssignKarigar = () => {
    if (getSelectedItemsCount() === 0) {
      toast.error("Please select at least one item");
      return;
    }

    // Prevent assigning when any selected item already has a karigar
    const selectedFlat = getSelectedItemsFlat();
    if (selectedFlat.some((item) => isKarigarAssigned(item))) {
      toast.error("Selected item(s) already have Karigar assigned");
      return;
    }

    // Get selected items with their order data
    const selectedItemsData = [];
    Object.keys(selectedItems).forEach((orderId) => {
      const order = orders.find((o) => String(o.id) === String(orderId));
      if (order && selectedItems[orderId]) {
        const selectedItemIds = Object.keys(selectedItems[orderId]);
        // Filter only selected items from the order
        const filteredItems =
          order.repairing_order_entry?.filter((item) =>
            selectedItemIds.includes(String(item.id)),
          ) || [];

        if (filteredItems.length > 0) {
          // Map repairing order fields to custom order fields for modal compatibility
          const mappedItems = filteredItems.map((item) => ({
            ...item,
            product_group:
              item.product_group ?? item.product_group_id ?? item.product_type, // Ensure AssignKarigarModal receives group id/key
            product_group_name:
              item.product_group_name || item.product_type || "",
            design: item.design || "",
            size: item.size || "",
            pcs: item.pcs || "",
            gwt: item.gwt || "",
            lwt: item.lwt || "",
            nwt: item.nwt || "",
            totalamt: item.totalamt || item.final_amount || "",
          }));

          selectedItemsData.push({
            ...order,
            custom_order_entry: mappedItems, // Use mapped items for modal compatibility
            repairing_order_entry: filteredItems, // Keep original for reference
            isRepairingOrder: true, // Flag to identify repairing orders
          });
        }
      }
    });

    // For the modal, we need to create a combined order data structure
    // If multiple orders are selected, we'll use the first order as base and combine items
    if (selectedItemsData.length > 0) {
      let combinedOrderData;

      if (selectedItemsData.length === 1) {
        // Single order, use as is
        combinedOrderData = selectedItemsData[0];
      } else {
        // Multiple orders, combine items
        combinedOrderData = {
          ...selectedItemsData[0],
          custom_order_entry: selectedItemsData.flatMap(
            (order) => order.custom_order_entry,
          ),
          repairing_order_entry: selectedItemsData.flatMap(
            (order) => order.repairing_order_entry,
          ),
          id: "bulk_" + Date.now(), // Create a unique ID for bulk operation
          isBulkAssignment: true,
          isRepairingOrder: true, // Flag to identify repairing orders
        };
      }

      setAssignKarigarOrderData(combinedOrderData);
      setShowAssignKarigarModal(true);
    }
  };

  // Handle assign karigar success
  const handleAssignKarigarSuccess = () => {
    // Clear selected items
    setSelectedItems({});

    // Refresh orders data to reflect changes immediately
    fetchOrders(currentPage, itemsPerPage, searchQuery, true);

    // Close modal
    setShowAssignKarigarModal(false);
    setAssignKarigarOrderData(null);
  };

  // Handle assign karigar modal close without success
  const handleAssignKarigarModalClose = () => {
    // Just close modal without clearing selected items
    setShowAssignKarigarModal(false);
    setAssignKarigarOrderData(null);
  };

  // Handle cancel order - check if items are selected
  const handleCancelOrder = () => {
    if (!canCancelOrder) {
      toast.error("You do not have permission to cancel orders.");
      return;
    }
    const selectedCount = getSelectedItemsCount();

    if (selectedCount > 0) {
      // Items are selected - cancel selected items
      const selectedItemsData = [];
      let firstOrder = null;

      Object.keys(selectedItems).forEach((orderId) => {
        const order = orders.find((o) => String(o.id) === String(orderId));
        if (order && selectedItems[orderId]) {
          if (!firstOrder) firstOrder = order;

          const selectedItemIds = Object.keys(selectedItems[orderId]);
          const filteredItems =
            order.repairing_order_entry?.filter((item) =>
              selectedItemIds.includes(String(item.id)),
            ) || [];

          filteredItems.forEach((item) => {
            selectedItemsData.push({
              id: item.id,
              tag_no: item.tag_no || "",
              product_group_name:
                item.product_group_name ||
                item.product_type ||
                `Product Group ${item.product_group}`,
            });
          });
        }
      });

      if (selectedItemsData.length > 0 && firstOrder) {
        setCancelOrderData(firstOrder);
        setCancelSelectedItems(selectedItemsData);
        setIsWholeOrderCancel(false);
        setShowCancelOrderModal(true);
      }
    } else {
      // No items selected - show message
      toast.error(
        "Please select at least one item to cancel, or use the cancel button in the order row to cancel the whole order",
      );
    }
  };

  // Handle cancel whole order (when clicking on a specific order row)
  const handleCancelWholeOrder = (order) => {
    if (!canCancelOrder) {
      toast.error("You do not have permission to cancel orders.");
      return;
    }
    setCancelOrderData(order);
    setCancelSelectedItems([]);
    setIsWholeOrderCancel(true);
    setShowCancelOrderModal(true);
  };

  // Handle cancel order success
  const handleCancelOrderSuccess = () => {
    setSelectedItems({});
    setShowCancelOrderModal(false);
    setCancelOrderData(null);
    setCancelSelectedItems([]);
    // Refetch immediately (do not rely only on socket)
    fetchOrders(currentPage, itemsPerPage, searchQuery, true);
  };

  // Handle bulk receive & update for selected items
  const handleBulkReceiveUpdate = () => {
    if (getSelectedItemsCount() === 0) {
      toast.error("Please select at least one item");
      return;
    }

    // Get selected items with their order data
    const selectedItemsData = [];
    Object.keys(selectedItems).forEach((orderId) => {
      const order = orders.find((o) => String(o.id) === String(orderId));
      if (order && selectedItems[orderId]) {
        const selectedItemIds = Object.keys(selectedItems[orderId]);
        // Filter only selected items from the order
        const filteredItems =
          order.repairing_order_entry?.filter((item) =>
            selectedItemIds.includes(String(item.id)),
          ) || [];

        if (filteredItems.length > 0) {
          // Map repairing order fields to custom order fields for modal compatibility
          const mappedItems = filteredItems.map((item) => ({
            ...item,
            product_group:
              item.product_group ?? item.product_group_id ?? item.product_type, // Ensure AssignKarigarModal receives group id/key
            product_group_name:
              item.product_group_name || item.product_type || "",
            design: item.design || "",
            size: item.size || "",
            pcs: item.pcs || "",
            gwt: item.gwt || "",
            lwt: item.lwt || "",
            nwt: item.nwt || "",
            totalamt: item.totalamt || item.final_amount || "",
          }));

          selectedItemsData.push({
            ...order,
            custom_order_entry: mappedItems, // Use mapped items for modal compatibility
            repairing_order_entry: filteredItems, // Keep original for reference
            isRepairingOrder: true, // Flag to identify repairing orders
          });
        }
      }
    });

    // For the modal, we need to create a combined order data structure
    if (selectedItemsData.length > 0) {
      let combinedOrderData;

      if (selectedItemsData.length === 1) {
        // Single order, use as is
        combinedOrderData = selectedItemsData[0];
      } else {
        // Multiple orders, combine items
        combinedOrderData = {
          ...selectedItemsData[0],
          custom_order_entry: selectedItemsData.flatMap(
            (order) => order.custom_order_entry,
          ),
          repairing_order_entry: selectedItemsData.flatMap(
            (order) => order.repairing_order_entry,
          ),
          id: "bulk_receive_" + Date.now(), // Create a unique ID for bulk operation
          isBulkReceive: true,
          isRepairingOrder: true, // Flag to identify repairing orders
        };
      }

      setReceiveUpdateOrderData(combinedOrderData);
      setShowReceiveUpdateModal(true);
    }
  };

  // Handle receive & update success
  const handleReceiveUpdateSuccess = () => {
    // Clear selected items
    setSelectedItems({});

    // Refresh orders data to reflect changes immediately
    fetchOrders(currentPage, itemsPerPage, searchQuery, true);

    // Close modal
    setShowReceiveUpdateModal(false);
    setReceiveUpdateOrderData(null);
  };

  // Handle receive & update modal close without success
  const handleReceiveUpdateModalClose = () => {
    // Just close modal without clearing selected items
    setShowReceiveUpdateModal(false);
    setReceiveUpdateOrderData(null);
  };

  // Memoize the PDF content
  const pdfContent = useMemo(() => {
    if (!selectedOrderForPdf) return null;

    return (
      <PDFViewer width="100%" height="100%">
        <RepairingSlipPDF
          slipData={{
            tokenNumber:
              selectedOrderForPdf.token_no ||
              selectedOrderForPdf.order_number ||
              selectedOrderForPdf.doc_number ||
              selectedOrderForPdf.id,
            date: formatDateToDDMMYYYY(
              selectedOrderForPdf.doc_date ||
              selectedOrderForPdf.created_at ||
              new Date().toISOString().split("T")[0],
            ),
            customer: {
              name:
                selectedOrderForPdf.name ||
                selectedOrderForPdf.customer_name ||
                selectedOrderForPdf.user_name ||
                "N/A",
              mobile:
                selectedOrderForPdf.mobile ||
                selectedOrderForPdf.customer_mobile ||
                selectedOrderForPdf.user_mobile ||
                "N/A",
              address:
                selectedOrderForPdf.address ||
                selectedOrderForPdf.customer_address ||
                "",
            },
            items: (selectedOrderForPdf.repairing_order_entry || []).map(
              (item, idx) => ({
                id: idx + 1,
                name: item.item_category_name || item.itemCategoryName || "",
                item_category_name:
                  item.item_category_name || item.itemCategoryName || "",
                issueDescription:
                  item.issue_description || item.description || "",
                currentWeight: item.current_weight || item.gwt || "0.000",
                expectedWeight:
                  item.exp_weight ||
                  item.expected_weight ||
                  item.nwt ||
                  "0.000",
                amount:
                  item.totalamt != null
                    ? item.totalamt
                    : item.final_amount != null
                      ? item.final_amount
                      : Number(item.lbr_amount || 0) +
                      Number(item.oth_amount || 0),
              }),
            ),
            expectedDelivery: formatDateToDDMMYYYY(
              selectedOrderForPdf.exp_delivery ||
              selectedOrderForPdf.expected_delivery ||
              "",
            ),
            finalAmount:
              selectedOrderForPdf.final_amount ||
              selectedOrderForPdf.grand_total ||
              "0.00",
            payments:
              selectedOrderForPdf.payments ||
              selectedOrderForPdf.payment_details ||
              [],
            note: selectedOrderForPdf.note || "",
          }}
        />
      </PDFViewer>
    );
  }, [selectedOrderForPdf]);

  return (
    <>
      <style>
        {`
          .item-checkbox {
            opacity: 0;
            transition: opacity 0.2s ease;
          }
          
          .item-column:hover .item-checkbox {
            opacity: 1;
          }
          
          .item-row:hover .item-checkbox {
            opacity: 1;
          }
          
          .item-checkbox:checked {
            opacity: 1 !important;
          }
        `}
      </style>
      <div className={`body-wrapper ${cardClass}`}>
        <div className="container-fluid">
          <div
            className={`card w-100 position-relative overflow-hidden ${cardClass}`}
          >
            <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center">
              <h4 className="card-title mb-0">Repair Order Table</h4>
              {getCurrentPinType() !== "quote" && canAddRepairingOrder && (
                <button
                  className={`btn btn-primary ${buttonClass}`}
                  disabled={!isCurrentFinancialYear}
                  title={
                    !isCurrentFinancialYear ? FY_ADD_DISABLED_TITLE : undefined
                  }
                  onClick={() => {
                    if (!isCurrentFinancialYear) return;
                    navigate("/repairingorderentry");
                  }}
                >
                  Add Repair Order
                </button>
              )}
            </div>

            <div className="card-body p-4">
              {/* Search and Filter Section */}
              <div className="d-flex justify-content-between mb-3">
                <input
                  type="text"
                  className={`form-control ${buttonClass}`}
                  style={{ maxWidth: "260px" }}
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                />
                <div className="d-flex align-items-center gap-2">
                  {getSelectedItemsCount() > 0 && (
                    <>
                      <button
                        className={`btn btn-primary ${buttonClass}`}
                        onClick={handleBulkAssignKarigar}
                        title={
                          canAssignSelectedItems()
                            ? `Assign Karigar (${getSelectedItemsCount()})`
                            : "Selected item(s) already have Karigar assigned"
                        }
                        disabled={!canAssignSelectedItems()}
                      >
                        Assign Karigar ({getSelectedItemsCount()})
                      </button>
                      <button
                        className={`btn btn-primary ${buttonClass}`}
                        onClick={handleBulkReceiveUpdate}
                      >
                        Receive & Update ({getSelectedItemsCount()})
                      </button>
                    </>
                  )}
                  <select
                    className="form-select w-auto"
                    style={{ maxWidth: "200px" }}
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setCurrentPage(1);
                    }}
                  >
                    <option value="">All Status</option>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              </div>{" "}
              {/* Table */}
              <div className={`table-responsive mb-4 border ${cardClass}`}>
                <table className={`table mb-0 align-middle ${tableClass}`}>
                  <thead className="text-dark fs-4">
                    <tr>
                      <th style={{ minWidth: "80px" }}>Order</th>
                      <th style={{ minWidth: "110px" }}>Created By</th>
                      <th style={{ minWidth: "120px" }}>Customer</th>
                      <th style={{ minWidth: "200px", maxWidth: "250px" }}>
                        Item
                      </th>
                      <th style={{ minWidth: "100px" }}>Delivery</th>
                      <th style={{ minWidth: "90px" }}>Amount</th>
                      <th style={{ minWidth: "80px" }}>Status</th>
                      <th style={{ minWidth: "120px" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      <tr>
                        <td colSpan="8" className="text-center">
                          Loading...
                        </td>
                      </tr>
                    ) : displayedOrders.length > 0 ? (
                      displayedOrders.map((order) => {
                        return (
                          <tr key={order.id}>
                            <td>{order.order_number || "N/A"}</td>
                            <td>{order.employee_name || "N/A"}</td>
                            <td>
                              {order.name || order.customer_name || "N/A"}
                            </td>
                            <td
                              className="item-column"
                              style={{ maxWidth: "250px", minWidth: "200px" }}
                            >
                              {(() => {
                                const items = order.repairing_order_entry;
                                const hasItems =
                                  items &&
                                  Array.isArray(items) &&
                                  items.length > 0;
                                if (!hasItems) return "No items";
                                return (
                                  <div style={{ maxWidth: "100%" }}>
                                    {items.map((item, index) => (
                                      <div
                                        key={item.id || index}
                                        className="mb-1 d-flex align-items-start item-row"
                                        style={{ maxWidth: "100%" }}
                                      >
                                        {!isItemCancelled(item) &&
                                          !isItemReceived(item) && (
                                            <input
                                              type="checkbox"
                                              className="form-check-input me-2 item-checkbox"
                                              style={{
                                                flexShrink: 0,
                                                marginTop: "2px",
                                              }}
                                              checked={isItemSelected(
                                                order.id,
                                                item.id,
                                              )}
                                              onChange={(e) =>
                                                handleItemSelect(
                                                  order.id,
                                                  item.id,
                                                  e.target.checked,
                                                )
                                              }
                                            />
                                          )}
                                        <span
                                          style={{
                                            wordWrap: "break-word",
                                            overflowWrap: "break-word",
                                            wordBreak: "break-word",
                                            whiteSpace: "normal",
                                            lineHeight: "1.3",
                                            fontSize: "0.9em",
                                            flex: 1,
                                          }}
                                        >
                                          <div className="d-flex align-items-center flex-wrap gap-2">
                                            <strong
                                              style={{
                                                opacity: isItemCancelled(item)
                                                  ? 0.6
                                                  : 1,
                                              }}
                                            >
                                              {item.tag_no
                                                ? `${item.tag_no}`
                                                : "No Tag"}
                                            </strong>
                                            {isItemReceived(item) &&
                                              !isItemCancelled(item) && (
                                                <span
                                                  className="badge bg-success-subtle text-success"
                                                  style={{ fontSize: "0.7em" }}
                                                >
                                                  ✓ Received
                                                </span>
                                              )}
                                            {!isItemReceived(item) &&
                                              isKarigarAssigned(item) &&
                                              !isItemCancelled(item) && (
                                                <span
                                                  className="badge bg-info-subtle text-info"
                                                  style={{ fontSize: "0.7em" }}
                                                >
                                                  Karigar Assigned
                                                </span>
                                              )}
                                            {isItemCancelled(item) && (
                                              <span
                                                className="badge bg-danger-subtle text-danger"
                                                style={{ fontSize: "0.7em" }}
                                              >
                                                Cancelled
                                              </span>
                                            )}
                                          </div>
                                          <div
                                            style={{
                                              opacity: isItemCancelled(item)
                                                ? 0.6
                                                : 1,
                                            }}
                                          >
                                            {item.product_group_name ||
                                              item.product_type ||
                                              item.product_group ||
                                              "Product Group"}
                                          </div>
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                );
                              })()}
                            </td>
                            <td>
                              {order.exp_delivery
                                ? formatDateToDDMMYYYY(order.exp_delivery)
                                : "N/A"}
                            </td>
                            <td>
                              {formatIndianRupee(order.final_amount || 0)}
                            </td>
                            <td>
                              {(() => {
                                const statusMeta = getOrderStatusMeta(order);
                                return (
                                  <span
                                    className={`badge ${statusMeta.className}`}
                                  >
                                    {statusMeta.label}
                                  </span>
                                );
                              })()}
                            </td>
                            <td>
                              <div className="d-flex gap-2">
                                <button
                                  className={`btn btn-primary btn-sm ${buttonClass}`}
                                  onClick={() => handleViewClick(order)}
                                  title="View Order"
                                >
                                  <Icon
                                    icon="solar:eye-bold"
                                    width="16"
                                    height="16"
                                  />
                                </button>
                                {(() => {
                                  const statusMeta = getOrderStatusMeta(order);
                                  const isOrderCompleted =
                                    statusMeta.label === "Completed";
                                  const isOrderCancelled =
                                    statusMeta.label === "Cancelled";
                                  const orderRemainingAmount =
                                    getRepairingOrderRemainingAmount(order);
                                  const productReceivedEligible =
                                    getProductReceivedEligibleItems(order);
                                  const receivedItems = Array.isArray(
                                    order.repairing_order_entry,
                                  )
                                    ? order.repairing_order_entry.filter(
                                      (item) => isItemReceived(item),
                                    )
                                    : [];
                                  const hasReceivedItems =
                                    receivedItems.length > 0;
                                  const moveToSalesTitle = isOrderCompleted
                                    ? "Order completed"
                                    : hasReceivedItems
                                      ? "Move to Sales"
                                      : "No received items available to move to Sales";

                                  return (
                                    <>
                                      <button
                                        className={`btn btn-primary btn-sm ${buttonClass}`}
                                        onClick={() =>
                                          handleOpenPdfModal(order)
                                        }
                                        title="View Slip PDF"
                                      >
                                        <Icon
                                          icon="solar:document-text-bold"
                                          width="16"
                                          height="16"
                                        />
                                      </button>
                                      <button
                                        className={`btn btn-primary btn-sm ${buttonClass}`}
                                        onClick={() =>
                                          handleOpenPaymentSettlementModal(
                                            order,
                                          )
                                        }
                                        title={
                                          isOrderCompleted
                                            ? "Order completed"
                                            : isOrderCancelled
                                              ? "Payment not available for cancelled order"
                                              : orderRemainingAmount > 0
                                                ? "Payment Settlement"
                                                : "No remaining amount"
                                        }
                                        disabled={
                                          isOrderCompleted ||
                                          orderRemainingAmount <= 0 ||
                                          isOrderCancelled
                                        }
                                      >
                                        <Icon
                                          icon="solar:wallet-bold"
                                          width="16"
                                          height="16"
                                        />
                                      </button>
                                      {productReceivedEligible.length > 0 ||
                                        isOrderCompleted ? (
                                        <button
                                          className={`btn btn-primary btn-sm ${buttonClass}`}
                                          onClick={() =>
                                            handleOpenProductReceivedModal(
                                              order,
                                            )
                                          }
                                          title={
                                            isOrderCompleted
                                              ? "Order completed"
                                              : "Product Delivery"
                                          }
                                          disabled={
                                            isOrderCompleted || isOrderCancelled
                                          }
                                        >
                                          <Icon
                                            icon="solar:box-bold"
                                            width="16"
                                            height="16"
                                          />
                                        </button>
                                      ) : null}
                                      {hasReceivedItems || isOrderCompleted ? (
                                        <button
                                          className={`btn btn-primary btn-sm ${buttonClass}`}
                                          onClick={() =>
                                            handleMoveToSales(order)
                                          }
                                          title={moveToSalesTitle}
                                          disabled={isOrderCompleted}
                                        >
                                          <Icon
                                            icon="solar:cart-large-bold"
                                            width="16"
                                            height="16"
                                          />
                                        </button>
                                      ) : canEditRepairingOrder ? (
                                        <button
                                          className={`btn btn-primary btn-sm ${buttonClass}`}
                                          onClick={() => handleEditClick(order)}
                                          title="Edit"
                                        >
                                          <Icon
                                            icon="solar:pen-bold"
                                            width="16"
                                            height="16"
                                          />
                                        </button>
                                      ) : null}
                                      <button
                                        className={`btn btn-primary btn-sm ${buttonClass}`}
                                        onClick={() =>
                                          handleCancelWholeOrder(order)
                                        }
                                        title={
                                          isOrderCompleted
                                            ? "Order completed"
                                            : !canCancelOrder
                                              ? "No permission to cancel orders"
                                              : "Cancel Order"
                                        }
                                        disabled={
                                          isOrderCompleted || !canCancelOrder
                                        }
                                      >
                                        <Icon
                                          icon="solar:close-circle-bold"
                                          width="16"
                                          height="16"
                                        />
                                      </button>
                                    </>
                                  );
                                })()}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="8" className="text-center">
                          No repairing orders found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {/* Pagination */}
              <Pagination
                totalItems={totalCount}
                itemsPerPage={itemsPerPage}
                currentPage={currentPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                itemsPerPageOptions={[10, 20, 30, 50, 100]}
              />
            </div>
          </div>
        </div>

        {/* View Repairing Order Modal */}
        <Modal
          show={showViewModal}
          onHide={() => setShowViewModal(false)}
          centered
          size="md"
        >
          <Modal.Header closeButton>
            <div className="d-flex w-100 align-items-center justify-content-between">
              <Modal.Title className="mb-0">
                Repairing Order Details
              </Modal.Title>
              {/* Order Number Badge */}
              {selectedOrder && (
                <span
                  className="badge bg-info-subtle text-info"
                  style={{ fontSize: "1em" }}
                >
                  {selectedOrder.order_number ||
                    selectedOrder.order_no ||
                    "N/A"}
                </span>
              )}
            </div>
          </Modal.Header>
          {/* Customer Name just below header */}
          {selectedOrder &&
            (() => {
              return (
                <div
                  className="ms-4 mt-2 mb-0"
                  style={{
                    fontSize: "1.08em",
                    fontWeight: 500,
                    textAlign: "left",
                    color: "#212529",
                  }}
                >
                  Name:{" "}
                  {selectedOrder.name || selectedOrder.customer_name || "N/A"}
                </div>
              );
            })()}
          <Modal.Body>
            {selectedOrder &&
              selectedOrder.repairing_order_entry &&
              Array.isArray(selectedOrder.repairing_order_entry) &&
              selectedOrder.repairing_order_entry.length > 0 ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1rem",
                }}
              >
                {selectedOrder.repairing_order_entry.map((item, idx) => (
                  <div
                    key={idx}
                    className="mb-3 p-2 border rounded-0 d-flex flex-column flex-md-row align-items-center"
                    style={{
                      color: "#212529",
                      justifyContent: "space-between",
                    }}
                  >
                    {/* Always render image container for consistent layout */}
                    <div
                      className="w-100 d-flex justify-content-center mb-2 mb-md-0 me-md-3"
                      style={{ minWidth: "100px", maxWidth: "100px" }}
                    >
                      {(() => {
                        const imagePath = item.image;
                        const fullImageUrl = imagePath
                          ? `${node_imageBaseUrl}/${imagePath}`.replace(
                            /([^:]\/)\/+/g,
                            "$1",
                          )
                          : "";
                        return fullImageUrl ? (
                          <img
                            src={fullImageUrl}
                            alt="Product Design"
                            style={{
                              maxWidth: "100px",
                              maxHeight: "100px",
                              objectFit: "contain",
                              borderRadius: "6px",
                              border: "1px solid #eee",
                            }}
                          />
                        ) : null;
                      })()}
                    </div>
                    <div style={{ minWidth: 0, flex: 1, textAlign: "left" }}>
                      <div>
                        <strong>Product Group:</strong>{" "}
                        {item.product_group_name ||
                          item.product_type ||
                          item.product_group ||
                          "N/A"}
                      </div>
                      <div>
                        <strong>Design:</strong> {item.design || "-"}
                      </div>
                      <div>
                        <strong>Size:</strong> {item.size || "-"}
                      </div>
                      <div>
                        <strong>Pieces:</strong> {item.pcs || "-"}
                      </div>
                      <div>
                        <strong>GWT:</strong>{" "}
                        {item.gwt || item.current_weight || "-"}
                      </div>
                      <div>
                        <strong>LWT:</strong> {item.lwt || "-"}
                      </div>
                      <div>
                        <strong>NWT:</strong>{" "}
                        {item.nwt ||
                          item.exp_weight ||
                          item.expected_weight ||
                          "-"}
                      </div>
                      <div>
                        <strong>Labor Amount:</strong>{" "}
                        {formatIndianRupee(
                          item.lbr_amount || item.labor_amount || 0,
                        )}
                      </div>
                      <div>
                        <strong>Other Amount:</strong>{" "}
                        {formatIndianRupee(
                          item.other_amount || item.other_charges || 0,
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div>No product details available.</div>
            )}
            {/* Order Total Amount */}
            {selectedOrder && (
              <div className="border-top mt-3 pt-3">
                <div className="row">
                  <div className="col-6">
                    <strong>Order Final Amount:</strong>
                  </div>
                  <div className="col-6 text-end">
                    <strong className="text-success fs-5">
                      {formatIndianRupee(selectedOrder.final_amount || 0)}
                    </strong>
                  </div>
                </div>
              </div>
            )}
          </Modal.Body>
        </Modal>

        {/* Assign Karigar Modal */}
        <AssignKarigarModal
          show={showAssignKarigarModal}
          onHide={handleAssignKarigarModalClose}
          orderData={assignKarigarOrderData}
          onAssignSuccess={handleAssignKarigarSuccess}
        />

        {/* Receive & Update Modal */}
        <ReceiveUpdateModal
          show={showReceiveUpdateModal}
          onHide={handleReceiveUpdateModalClose}
          orderData={receiveUpdateOrderData}
          onReceiveSuccess={handleReceiveUpdateSuccess}
          masterGoldRate={masterGoldRate}
          masterSilverRate={masterSilverRate}
        />

        <ProductReceivedModal
          show={showProductReceivedModal}
          onHide={() => {
            setShowProductReceivedModal(false);
            setProductReceivedOrder(null);
            setProductReceivedItems([]);
          }}
          order={productReceivedOrder}
          eligibleItems={productReceivedItems}
          bankAccountsList={bankAccountsList}
          buttonClass={buttonClass}
          cardClass={cardClass}
          onSuccess={handleProductReceivedSuccess}
        />

        {/* Cancel Order Modal */}
        <CancelOrderModal
          show={showCancelOrderModal}
          onHide={() => {
            setShowCancelOrderModal(false);
            setCancelOrderData(null);
            setCancelSelectedItems([]);
          }}
          orderData={cancelOrderData}
          selectedItems={cancelSelectedItems}
          isWholeOrder={isWholeOrderCancel}
          orderType="repairing"
          onSuccess={handleCancelOrderSuccess}
          bankAccountsList={bankAccountsList}
        />

        {/* PDF Modal */}
        <Modal
          show={showPdfModal}
          onHide={() => {
            setShowPdfModal(false);
            setSelectedOrderForPdf(null);
          }}
          size="xl"
          centered
          dialogClassName="pdf-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>Repairing Slip PDF Preview</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ height: "80vh", overflow: "auto" }}>
            {isPdfLoading ? (
              <div className="h-100 d-flex align-items-center justify-content-center">
                Loading PDF data...
              </div>
            ) : (
              pdfContent
            )}
          </Modal.Body>
        </Modal>

        <Modal
          show={showPaymentSettlementModal}
          onHide={handleClosePaymentSettlementModal}
          centered
          size="xl"
          backdrop="static"
          keyboard={false}
        >
          <Modal.Header closeButton>
            <Modal.Title>Payment Settlement</Modal.Title>
          </Modal.Header>
          <Modal.Body className="pt-3 pb-1 px-4">
            {isSettlementLoading ? (
              <div className="d-flex justify-content-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <>
                <div className="row g-3 mb-3">
                  <div className="col-md-4">
                    <div className={`h-100 border ${buttonClass} bg-white p-3`}>
                      <div className="text-uppercase small fw-semibold text-dark">
                        Total Amount
                      </div>
                      <div className="fw-bold fs-5 mt-1 ">
                        {formatIndianRupee(settlementTotalAmount)}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className={`h-100 border ${buttonClass} bg-white p-3`}>
                      <div className="text-uppercase small fw-semibold text-dark">
                        Remaining Amount
                      </div>
                      <div
                        className="fw-bold fs-5 mt-1"
                        style={{ color: "var(--theme-primary)" }}
                      >
                        {formatIndianRupee(settlementRemainingAmount)}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className={`h-100 border ${buttonClass} bg-white p-3`}>
                      <div className="text-uppercase small fw-semibold text-dark">
                        New Payment Total
                      </div>
                      <div className="fw-bold fs-5 mt-1">
                        {formatIndianRupee(newSettlementAmountTotal)}
                      </div>
                    </div>
                  </div>
                </div>

                {hasExistingSettlementPayments ? (
                  <div className="mb-3">
                    <div
                      className={`d-flex justify-content-between align-items-center border ${buttonClass} px-3 py-2 mb-2 bg-white`}
                    >
                      <h6 className="fw-semibold mb-0 text-dark text-uppercase small">
                        Previous Payments
                      </h6>
                      <button
                        type="button"
                        className={`btn btn-outline-secondary btn-sm d-flex align-items-center gap-1 ${buttonClass}`}
                        onClick={() =>
                          setIsPreviousPaymentsExpanded((prev) => !prev)
                        }
                      >
                        <Icon
                          icon={
                            isPreviousPaymentsExpanded
                              ? "solar:eye-closed-bold"
                              : "solar:eye-bold"
                          }
                          width={18}
                          height={18}
                        />
                        {isPreviousPaymentsExpanded ? "Hide" : "View"}
                      </button>
                    </div>
                    {isPreviousPaymentsExpanded && (
                      <div
                        className={`table-responsive rounded-0 border ${buttonClass} overflow-hidden`}
                      >
                        <table className="table table-sm mb-0 align-middle">
                          <thead className="bg-body-tertiary text-secondary small text-uppercase">
                            <tr>
                              <th
                                className="text-center"
                                style={{ width: "60px" }}
                              >
                                #
                              </th>
                              <th style={{ width: "140px" }}>Date</th>
                              <th>Mode</th>
                              <th>Type</th>
                              <th>Remarks</th>
                              <th
                                className="text-end"
                                style={{ width: "140px" }}
                              >
                                Amt
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {existingSettlementPayments.map(
                              (payment, index) => {
                                const paymentDate =
                                  payment.created_at ||
                                  payment.payment_date ||
                                  payment.transaction_date ||
                                  payment.date ||
                                  "";
                                const formattedDate = paymentDate
                                  ? formatDateToDDMMYYYY(
                                    String(paymentDate).split(/[ T]/)[0],
                                  )
                                  : "-";
                                return (
                                  <tr key={payment.id || index}>
                                    <td className="text-center">{index + 1}</td>
                                    <td>{formattedDate}</td>
                                    <td>
                                      {payment.payment_mode ||
                                        payment.pay_mode ||
                                        payment.method ||
                                        "-"}
                                    </td>
                                    <td>
                                      {getBankNameById(
                                        payment.bank_account_id ??
                                        payment.bank_type,
                                      )}
                                    </td>
                                    <td>{payment.remarks || "-"}</td>
                                    <td className="text-end">
                                      {formatIndianRupee(
                                        parseNumericValue(
                                          payment.amount ??
                                          payment.aavak_amt ??
                                          payment.total_amount ??
                                          payment.amt ??
                                          0,
                                        ),
                                      )}
                                    </td>
                                  </tr>
                                );
                              },
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="mb-3 small text-muted">
                    No previous settlement payments recorded for this order.
                  </div>
                )}

                <h6 className="fw-semibold mb-2 text-dark text-uppercase small">
                  Add New Payments
                </h6>
                <div
                  className={`table-responsive rounded-0 border ${buttonClass} overflow-hidden`}
                >
                  <table className="table table-sm align-middle mb-0">
                    <thead className="bg-primary-subtle text-secondary small text-uppercase">
                      <tr>
                        <th className="text-center" style={{ width: "60px" }}>
                          #
                        </th>
                        <th>Mode </th>
                        <th style={{ width: "220px" }}>Type </th>
                        <th style={{ minWidth: "160px" }}>Remarks</th>
                        <th className="text-end" style={{ width: "150px" }}>
                          Amt 
                        </th>
                        <th className="text-center" style={{ width: "80px" }}>
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {settlementRows.map((row, index) => (
                        <tr key={row.id}>
                          <td className="text-center">{index + 1}</td>
                          <td>
                            <Select
                              options={SETTLEMENT_METHOD_OPTIONS}
                              value={
                                SETTLEMENT_METHOD_OPTIONS.find(
                                  (opt) => opt.value === row.pay_mode,
                                ) || null
                              }
                              onChange={(option) =>
                                handleSettlementRowChange(
                                  row.id,
                                  "pay_mode",
                                  option ? option.value : "",
                                )
                              }
                              placeholder="Select Method"
                              menuPortalTarget={document.body}
                              styles={{
                                menuPortal: (base) => ({
                                  ...base,
                                  zIndex: 9999,
                                }),
                                control: (base) => ({
                                  ...base,
                                  minHeight: "31px",
                                  height: "31px",
                                }),
                              }}
                              classNamePrefix="react-select"
                            />
                          </td>
                          <td>
                            {row.pay_mode === "kasar" ? (
                              <span className="text-muted small px-1">—</span>
                            ) : (
                              <Select
                                options={bankOptions}
                                value={
                                  bankOptions.find(
                                    (opt) => opt.value === row.type,
                                  ) || null
                                }
                                onChange={(option) =>
                                  handleSettlementRowChange(
                                    row.id,
                                    "type",
                                    option ? option.value : "",
                                  )
                                }
                                placeholder="Select Type"
                                menuPortalTarget={document.body}
                                styles={{
                                  menuPortal: (base) => ({
                                    ...base,
                                    zIndex: 9999,
                                  }),
                                  control: (base) => ({
                                    ...base,
                                    minHeight: "31px",
                                    height: "31px",
                                  }),
                                }}
                                classNamePrefix="react-select"
                              />
                            )}
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              className={`shadow-none ${buttonClass}`}
                              type="text"
                              placeholder="Remarks"
                              value={row.remarks}
                              onChange={(e) =>
                                handleSettlementRowChange(
                                  row.id,
                                  "remarks",
                                  e.target.value,
                                )
                              }
                            />
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              className={`shadow-none text-end ${buttonClass}`}
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder="0.00"
                              value={row.amount}
                              onChange={(e) =>
                                handleSettlementRowChange(
                                  row.id,
                                  "amount",
                                  e.target.value,
                                )
                              }
                              onFocus={() =>
                                handleSettlementAmountFocus(row.id)
                              }
                            />
                          </td>
                          <td className="text-center">
                            <Button
                              variant="outline-secondary"
                              style={{
                                borderColor: "var(--theme-primary)",
                                color: "var(--theme-primary)",
                              }}
                              size="sm"
                              disabled={settlementRows.length === 1}
                              onClick={() => handleSettlementRowRemove(row.id)}
                            >
                              <Icon
                                icon="solar:trash-bin-minimalistic-bold"
                                width={16}
                                height={16}
                                style={{ color: "var(--theme-primary)" }}
                              />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </Modal.Body>
          <Modal.Footer className="justify-content-end">
            <Button
              variant="primary"
              className={buttonClass}
              onClick={handleSavePaymentSettlement}
              disabled={disableSettlementSaveButton}
            >
              {isSettlementSaving ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                  />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
}

export default RepairingOrder;
